import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Modal, Platform, Pressable, ScrollView, Switch, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { attachQuoteDocumentApi, requestProtectionQuoteApi } from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { DateField } from '@/components/ui/date-field';
import { IconTile } from '@/components/ui/icon-tile';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { StatusChip } from '@/components/ui/status-chip';
import { TextField } from '@/components/ui/text-field';
import { useAuthStore } from '@/store/auth-store';
import { usePoliciesStore } from '@/store/policies-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { Policy, PolicyType } from '@/types/domain';
import { daysUntil, formatDate, formatPln } from '@/utils/format';

const TYPE_ICON: Record<Policy['type'], keyof typeof Ionicons.glyphMap> = {
  CAR_OC: 'car-outline',
  CAR_AC: 'car-sport-outline',
  HOME: 'home-outline',
  LIFE: 'heart-outline',
  NNW: 'body-outline',
  TRAVEL: 'airplane-outline',
  HEALTH: 'medkit-outline',
  OTHER: 'document-text-outline',
};

const TYPES: { key: PolicyType; label: string }[] = [
  { key: 'CAR_OC', label: 'OC auto' },
  { key: 'CAR_AC', label: 'AC auto' },
  { key: 'HOME', label: 'Dom / mieszkanie' },
  { key: 'LIFE', label: 'Życie' },
  { key: 'NNW', label: 'NNW' },
  { key: 'TRAVEL', label: 'Podróż' },
  { key: 'HEALTH', label: 'Zdrowie' },
  { key: 'OTHER', label: 'Inna' },
];

const IS_IMAGE = (uri: string) => /\.(jpe?g|png|heic|webp|gif)$/i.test(uri) || uri.startsWith('data:image');

// Content-type the upload endpoint accepts (image/jpeg|png|webp|pdf). Unknown
// image extensions fall back to jpeg so the server does not reject the scan.
const UPLOAD_MIME = (uri: string): string => {
  const m = uri.toLowerCase().match(/\.(jpe?g|png|webp|pdf)(?:\?|$)/);
  const ext = m?.[1];
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return 'image/jpeg';
};

function parseZl(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

/**
 * Policy details — read-only view of every field the wallet holds, PLUS (owner
 * requests): an "Edytuj" mode for locally-added policies, a document section
 * (preview a scan/PDF if attached, or add one), and a "get an offer from
 * Polfinance" CTA so an ending policy isn't a dead end. Polfinance policies from
 * the CRM stay read-only (they're never shown on this route).
 */
export default function PolicyDetailsScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const policies = usePoliciesStore((s) => s.policies);
  const updatePolicy = usePoliciesStore((s) => s.updatePolicy);
  const addPolicyDocument = usePoliciesStore((s) => s.addPolicyDocument);
  const removePolicyDocument = usePoliciesStore((s) => s.removePolicyDocument);
  const removePolicy = usePoliciesStore((s) => s.removePolicy);
  const account = useAuthStore((s) => s.account);

  const policy = policies.find((p) => p.id === id);

  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [viewUri, setViewUri] = useState<string | null>(null);
  // Renewal-quote request state. The consent modal is the important bit: the app
  // promises data stays local, so before anything leaves for Polfinance the user
  // must knowingly agree (owner: "żeby klient nie zdziwił się, że nagle ma ofertę").
  const [quoteModal, setQuoteModal] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);
  const [quoteBusy, setQuoteBusy] = useState(false);

  // Edit form (populated when entering edit mode).
  const [type, setType] = useState<PolicyType>('CAR_OC');
  const [insurer, setInsurer] = useState('');
  const [number, setNumber] = useState('');
  const [customName, setCustomName] = useState('');
  const [premiumRaw, setPremiumRaw] = useState('');
  const [openEnded, setOpenEnded] = useState(false);
  const [startIso, setStartIso] = useState<string | null>(null);
  const [endIso, setEndIso] = useState<string | null>(null);
  const [techIso, setTechIso] = useState<string | null>(null);

  if (!policy) return null;
  const editable = policy.source !== 'POLFINANCE';
  const isMotor = type === 'CAR_OC' || type === 'CAR_AC';

  const days = policy.endDate ? daysUntil(policy.endDate) : null;
  const endingSoon = days !== null && days < 30;

  let elapsedPct: number | null = null;
  if (policy.startDate && policy.endDate) {
    const total = daysUntil(policy.endDate) - daysUntil(policy.startDate);
    if (total > 0) elapsedPct = Math.max(0, Math.min(100, ((total - days!) / total) * 100));
  }

  const na = t('policyDetails.notSet');
  const rows: { label: string; value: string }[] = [
    { label: t('policyDetails.type'), value: t(`policyType.${policy.type}`) },
    { label: t('policyDetails.insurer'), value: policy.insurerName || na },
    { label: t('policyDetails.number'), value: policy.policyNumber || na },
  ];
  if (policy.subject) rows.push({ label: t('policyDetails.subject'), value: policy.subject });
  rows.push(
    {
      label: t('policyDetails.premium'),
      value: `${formatPln(policy.premiumGr)} ${t(`premiumPeriod.${policy.premiumPeriod}`)}`,
    },
    { label: t('policyDetails.start'), value: policy.startDate ? formatDate(policy.startDate) : na },
    {
      label: t('policyDetails.end'),
      value: policy.openEnded || !policy.endDate ? t('policies.openEnded') : formatDate(policy.endDate),
    },
  );
  if (policy.nextTechnicalExamDate) {
    rows.push({ label: t('policyDetails.techExam'), value: formatDate(policy.nextTechnicalExamDate) });
  }
  rows.push({ label: t('policyDetails.source'), value: t(`policySource.${policy.source}`) });

  const startEdit = () => {
    setType(policy.type);
    setInsurer(policy.insurerName ?? '');
    setNumber(policy.policyNumber ?? '');
    setCustomName(policy.customName ?? '');
    setPremiumRaw(policy.premiumGr ? String(Math.round(policy.premiumGr / 100)) : '');
    setOpenEnded(policy.openEnded);
    setStartIso(policy.startDate);
    setEndIso(policy.endDate);
    setTechIso(policy.nextTechnicalExamDate);
    setEditing(true);
  };

  const saveEdit = async () => {
    await updatePolicy(policy.id, {
      type,
      insurerName: insurer.trim(),
      policyNumber: number.trim() || null,
      customName: customName.trim() || null,
      premiumGr: parseZl(premiumRaw),
      openEnded,
      startDate: startIso,
      endDate: openEnded ? null : endIso,
      // Leaving the motor types drops the inspection date (and its reminder).
      nextTechnicalExamDate: isMotor ? techIso : null,
    });
    setEditing(false);
  };

  const onDelete = async () => {
    await removePolicy(policy.id);
    router.back();
  };

  // Attach a scan: photo (camera/gallery) or a PDF/file from storage.
  const addPhoto = async (fromCamera: boolean) => {
    if (fromCamera) {
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      if (!perm.granted) return;
    }
    const picked = fromCamera
      ? await ImagePicker.launchCameraAsync({ mediaTypes: ['images'], quality: 0.9 })
      : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.9 });
    if (picked.canceled || !picked.assets?.length) return;
    await addPolicyDocument(policy.id, picked.assets[0].uri);
  };
  const addFile = async () => {
    const picked = await DocumentPicker.getDocumentAsync({
      type: ['application/pdf', 'image/*'],
      copyToCacheDirectory: true,
      multiple: false,
    });
    if (picked.canceled || !picked.assets?.length) return;
    await addPolicyDocument(policy.id, picked.assets[0].uri);
  };

  const openDoc = async (uri: string) => {
    if (IS_IMAGE(uri)) {
      setViewUri(uri);
      return;
    }
    // Non-image (PDF): open in the system/browser viewer (best effort).
    try {
      const WebBrowser = await import('expo-web-browser');
      await WebBrowser.openBrowserAsync(uri);
    } catch {
      const { Linking } = await import('react-native');
      await Linking.openURL(uri).catch(() => undefined);
    }
  };

  // "Get an offer from Polfinance": a guest connects first; a logged-in user
  // sees the consent modal, then we send the policy data to the caretaker agent.
  const onQuoteCta = () => {
    if (!account) {
      router.push('/agent');
      return;
    }
    setQuoteModal(true);
  };
  const sendQuote = async () => {
    setQuoteBusy(true);
    const period = t(`premiumPeriod.${policy.premiumPeriod}`);
    const summary =
      `Prośba o ofertę / wznowienie z aplikacji (klient świadomie zgodził się wysłać dane tej polisy).\n` +
      `Rodzaj: ${t(`policyType.${policy.type}`)}.\n` +
      `Towarzystwo: ${policy.insurerName || '—'}.\n` +
      `Nr polisy: ${policy.policyNumber || '—'}.\n` +
      `Składka: ${formatPln(policy.premiumGr)} ${period}.\n` +
      `Koniec ochrony: ${policy.openEnded || !policy.endDate ? 'bezterminowo' : formatDate(policy.endDate)}.\n` +
      (policy.documentUris.length > 0 ? `Klient ma załączony dokument tej polisy w aplikacji.\n` : '');
    const res = await requestProtectionQuoteApi(summary).catch(() => undefined);
    // The consent modal already told the client we send the attached document —
    // now actually upload it to the just-created lead so the agent sees the real
    // policy (until now only the read-out text reached the CRM). Best-effort.
    const salesLeadId = res?.data?.salesLeadId;
    if (salesLeadId && policy.documentUris.length > 0) {
      for (const uri of policy.documentUris) {
        await attachQuoteDocumentApi(salesLeadId, {
          uri,
          name: uri.split('/').pop() || 'polisa.jpg',
          mimeType: UPLOAD_MIME(uri),
        }).catch(() => undefined);
      }
    }
    setQuoteBusy(false);
    setQuoteModal(false);
    setQuoteSent(true);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader
          title={t('policyDetails.title')}
          rightIcon={editable && !editing ? 'pencil-outline' : undefined}
          onRightPress={editable && !editing ? startEdit : undefined}
        />

        {/* Identity header */}
        <Card style={{ gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm }}>
            <IconTile
              icon={TYPE_ICON[policy.type]}
              bg={policy.type === 'LIFE' ? colors.navy : colors.primaryTile}
              fg={policy.type === 'LIFE' ? '#FFFFFF' : colors.navy}
            />
            <View style={{ flex: 1 }}>
              <AppText variant="headlineSm">{policy.customName ?? policy.insurerName}</AppText>
              <AppText variant="bodyMd">{t('policies.insurer', { name: policy.insurerName })}</AppText>
            </View>
            <StatusChip
              kind={endingSoon ? 'warning' : 'success'}
              icon={endingSoon ? 'time-outline' : 'checkmark-circle-outline'}
              label={endingSoon ? t('policies.statusEndingSoon') : t('policies.statusActive')}
            />
          </View>

          {elapsedPct !== null && days !== null && (
            <View style={{ gap: spacing.base }}>
              <View style={{ height: 8, borderRadius: radius.full, backgroundColor: colors.surface, overflow: 'hidden' }}>
                <View
                  style={{
                    height: 8,
                    width: `${elapsedPct}%`,
                    borderRadius: radius.full,
                    backgroundColor: endingSoon ? colors.error : colors.success,
                  }}
                />
              </View>
              <AppText variant="labelMd" color={endingSoon ? colors.error : colors.textSecondary}>
                {t('common.daysLeft', { count: days })}
              </AppText>
            </View>
          )}
        </Card>

        {editing ? (
          /* ---- Edit form (local policies only) ---- */
          <Card style={{ gap: spacing.sm }}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
              {TYPES.map(({ key, label }) => {
                const active = type === key;
                return (
                  <Pressable
                    key={key}
                    accessibilityRole="button"
                    onPress={() => setType(key)}
                    style={{
                      backgroundColor: active ? colors.navy : colors.background,
                      borderRadius: radius.full,
                      paddingHorizontal: spacing.md,
                      paddingVertical: spacing.xs,
                    }}
                  >
                    <AppText variant="labelLg" color={active ? '#FFFFFF' : colors.navy}>
                      {label}
                    </AppText>
                  </Pressable>
                );
              })}
            </View>
            <TextField label={t('newPolicy.insurer')} placeholder="np. PZU" value={insurer} onChangeText={setInsurer} />
            <TextField
              label={t('newPolicy.premium')}
              placeholder="np. 1 200"
              keyboardType="numeric"
              value={premiumRaw}
              onChangeText={setPremiumRaw}
            />
            {/* Start of protection — drives the time-progress bar on the card. */}
            <DateField label={t('newPolicy.startDate')} value={startIso} onChange={setStartIso} />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 44 }}>
              <AppText variant="bodyLg" style={{ flex: 1 }}>
                {t('newPolicy.openEnded')}
              </AppText>
              <Switch
                value={openEnded}
                onValueChange={setOpenEnded}
                trackColor={{ false: colors.disabledBg, true: colors.primary }}
                thumbColor="#FFFFFF"
              />
            </View>
            {!openEnded && <DateField label={t('newPolicy.endDate')} value={endIso} onChange={setEndIso} />}
            {/* Technical inspection date — motor policies only, feeds a TECH_EXAM reminder. */}
            {isMotor && <DateField label={t('newPolicy.techExamDate')} value={techIso} onChange={setTechIso} />}
            <TextField
              label={t('newPolicy.number')}
              placeholder={t('newPolicy.numberPlaceholder')}
              value={number}
              onChangeText={setNumber}
            />
            <TextField
              label={t('newPolicy.customName')}
              placeholder={t('newPolicy.customNamePlaceholder')}
              value={customName}
              onChangeText={setCustomName}
            />
            <PrimaryButton
              label={t('policyDetails.saveEdit')}
              disabled={!insurer.trim() || parseZl(premiumRaw) <= 0 || (!openEnded && !endIso)}
              onPress={saveEdit}
            />
            <PrimaryButton label={t('common.cancel')} variant="secondary" onPress={() => setEditing(false)} />
          </Card>
        ) : (
          <>
            {/* Field rows */}
            <Card style={{ padding: 0 }}>
              {rows.map((row, idx) => (
                <View
                  key={row.label}
                  style={{
                    flexDirection: 'row',
                    gap: spacing.sm,
                    padding: spacing.md,
                    borderTopWidth: idx === 0 ? 0 : 1,
                    borderTopColor: colors.divider,
                  }}
                >
                  <AppText variant="bodyMd" style={{ flex: 1 }}>
                    {row.label}
                  </AppText>
                  <AppText variant="labelLg" style={{ flex: 1, textAlign: 'right' }}>
                    {row.value}
                  </AppText>
                </View>
              ))}
            </Card>

            {/* Documents — preview an attached scan/PDF, or add one */}
            <Card style={{ gap: spacing.sm }}>
              <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                {t('policyDetails.documents')}
              </AppText>
              {policy.documentUris.length === 0 ? (
                <AppText variant="bodyMd">{t('policyDetails.noDocument')}</AppText>
              ) : (
                policy.documentUris.map((uri) => (
                  <View
                    key={uri}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 56 }}
                  >
                    {IS_IMAGE(uri) ? (
                      <Image
                        source={{ uri }}
                        style={{ width: 44, height: 44, borderRadius: radius.chip, backgroundColor: colors.surface }}
                      />
                    ) : (
                      <IconTile icon="document-text-outline" bg={colors.primaryTile} fg={colors.navy} size={44} />
                    )}
                    <Pressable accessibilityRole="button" onPress={() => openDoc(uri)} style={{ flex: 1, minHeight: 44, justifyContent: 'center' }}>
                      <AppText variant="labelLg" color={colors.primary}>
                        {IS_IMAGE(uri) ? t('policyDetails.viewScan') : t('policyDetails.openDocument')}
                      </AppText>
                    </Pressable>
                    {editable && (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={t('policyDetails.removeDocument')}
                        onPress={() => removePolicyDocument(policy.id, uri)}
                        style={{ minWidth: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Ionicons name="trash-outline" size={18} color={colors.error} />
                      </Pressable>
                    )}
                  </View>
                ))
              )}
              {editable && (
                <View style={{ gap: spacing.xs }}>
                  <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                    {Platform.OS !== 'web' && (
                      <PrimaryButton label={t('policyDetails.addPhoto')} onPress={() => addPhoto(true)} style={{ flex: 1 }} />
                    )}
                    <PrimaryButton
                      label={Platform.OS === 'web' ? t('policyDetails.addDocument') : t('policyDetails.addFromGallery')}
                      variant={Platform.OS === 'web' ? 'primary' : 'secondary'}
                      onPress={() => addPhoto(false)}
                      style={{ flex: 1 }}
                    />
                  </View>
                  <PrimaryButton label={t('policyDetails.addPdf')} variant="secondary" onPress={addFile} />
                </View>
              )}
            </Card>

            {/* Renewal / offer CTA — an ending (or any) policy shouldn't be a dead
                end: Polfinance can quote a renewal, maybe cheaper (owner). */}
            <Card style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                <Ionicons name="pricetags-outline" size={22} color={colors.primary} />
                <AppText variant="labelLg" color={colors.navy} style={{ flex: 1 }}>
                  {endingSoon ? t('policyDetails.offerTitleEnding') : t('policyDetails.offerTitle')}
                </AppText>
              </View>
              <AppText variant="bodyMd">{t('policyDetails.offerBody')}</AppText>
              {quoteSent ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                  <Ionicons name="checkmark-circle" size={18} color={colors.success} />
                  <AppText variant="labelMd" color={colors.success}>
                    {t('policyDetails.offerSent')}
                  </AppText>
                </View>
              ) : (
                <PrimaryButton label={t('policyDetails.offerCta')} onPress={onQuoteCta} />
              )}
            </Card>

            {/* Guarded delete — two-tap so a stray press never wipes a policy */}
            {!confirming ? (
              <PrimaryButton label={t('policyDetails.delete')} variant="secondary" onPress={() => setConfirming(true)} />
            ) : (
              <Card style={{ gap: spacing.md, borderWidth: 1, borderColor: colors.error }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                  <Ionicons name="warning-outline" size={22} color={colors.error} />
                  <AppText variant="labelLg" style={{ flex: 1 }}>
                    {t('policyDetails.deleteConfirm')}
                  </AppText>
                </View>
                <PrimaryButton label={t('policyDetails.deleteYes')} onPress={onDelete} style={{ backgroundColor: colors.error }} />
                <PrimaryButton label={t('common.cancel')} variant="secondary" onPress={() => setConfirming(false)} />
              </Card>
            )}
          </>
        )}
      </ScrollView>

      {/* Consent modal — data leaves the phone only after a knowing "Rozumiem" */}
      <Modal visible={quoteModal} transparent animationType="fade" onRequestClose={() => setQuoteModal(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(3,24,64,0.72)', justifyContent: 'center', padding: spacing.screen }}>
          <Card style={{ gap: spacing.md }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name="paper-plane-outline" size={24} color={colors.primary} />
              <AppText variant="headlineSm" style={{ flex: 1 }}>
                {t('policyDetails.offerConsentTitle')}
              </AppText>
            </View>
            <AppText variant="bodyLg" color={colors.textSecondary}>
              {policy.documentUris.length > 0
                ? t('policyDetails.offerConsentBodyDoc')
                : t('policyDetails.offerConsentBody')}
            </AppText>
            <PrimaryButton
              label={quoteBusy ? t('policyDetails.offerSending') : t('policyDetails.offerConsentYes')}
              disabled={quoteBusy}
              onPress={sendQuote}
            />
            <PrimaryButton
              label={t('policyDetails.offerConsentNo')}
              variant="secondary"
              disabled={quoteBusy}
              onPress={() => setQuoteModal(false)}
            />
          </Card>
        </View>
      </Modal>

      {/* Full-screen image preview */}
      <Modal visible={viewUri !== null} transparent animationType="fade" onRequestClose={() => setViewUri(null)}>
        <Pressable
          onPress={() => setViewUri(null)}
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.9)', padding: spacing.md, justifyContent: 'center' }}
        >
          {viewUri && <Image source={{ uri: viewUri }} style={{ flex: 1, borderRadius: radius.card }} resizeMode="contain" />}
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}
